import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import LibraryStudentCard from '../../admin/library/components/LibraryStudentCard';
import { getGradeLevelFromClassName, getPeriodStats, getTopGrowthStudentId } from '../../admin/library/utils/libraryScoring';

const periods = [
  { key: 'week', label: 'Bu Hafta' },
  { key: 'month', label: 'Bu Ay' },
  { key: 'schoolYear', label: 'Bu Öğrenim Yılı' },
];

export default function TeacherLibraryBoard({ students = [], classCode }) {
  const [periodKey, setPeriodKey] = useState('week');
  const gradeLevel = getGradeLevelFromClassName(classCode);
  const sortedStudents = useMemo(() => [...students].sort((a, b) => {
    const aStats = getPeriodStats(a, periodKey);
    const bStats = getPeriodStats(b, periodKey);
    if (bStats.pages !== aStats.pages) return bStats.pages - aStats.pages;
    if (bStats.books !== aStats.books) return bStats.books - aStats.books;
    return (a.firstName || '').localeCompare(b.firstName || '', 'tr-TR');
  }), [students, periodKey]);
  const topGrowthStudentId = useMemo(() => getTopGrowthStudentId(students, periodKey), [students, periodKey]);
  const totals = useMemo(() => sortedStudents.reduce((acc, student) => {
    const stats = getPeriodStats(student, periodKey);
    acc.pages += stats.pages;
    acc.books += stats.books;
    if (stats.pages > 0) acc.activeReaders += 1;
    return acc;
  }, { pages: 0, books: 0, activeReaders: 0 }), [sortedStudents, periodKey]);

  return (
    <Box sx={{ mb: 3 }}>
      <Paper elevation={3} sx={{ borderRadius: 4, p: { xs: 1.2, sm: 1.5, md: 2 }, bgcolor: 'rgba(255,255,255,0.86)', mb: 2 }}>
        <Tabs value={periodKey} onChange={(_, value) => setPeriodKey(value)} textColor="inherit" indicatorColor="secondary" variant="fullWidth" sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, fontSize: { xs: 13, md: 20 } } }}>
          {periods.map((period) => <Tab key={period.key} value={period.key} label={period.label} />)}
        </Tabs>
      </Paper>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          ['Toplam Sayfa', totals.pages],
          ['Bitirilen Kitap', totals.books],
          ['Aktif Okur', totals.activeReaders],
        ].map(([label, value]) => <Grid item xs={4} key={label}>
          <Paper elevation={2} sx={{ borderRadius: 4, p: { xs: 1.5, md: 2 }, bgcolor: 'rgba(255,255,255,0.8)' }}>
            <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: 'clamp(14px, 1.1vw, 18px)' }}>{label}</Typography>
            <Typography sx={{ fontWeight: 900, fontSize: 'clamp(28px, 2.6vw, 46px)', lineHeight: 1.1 }}>{value}</Typography>
          </Paper>
        </Grid>)}
      </Grid>
      {sortedStudents.length === 0 && <Typography sx={{ textAlign: 'center', py: 4 }}>Bu sınıfta öğrenci bulunamadı.</Typography>}
      <Grid container rowSpacing={{ xs: 2.6, md: 3.2 }} columnSpacing={{ xs: 2.6, md: 3.2 }}>
        {sortedStudents.map((student) => <Grid key={student.id} item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 560 }}>
            <LibraryStudentCard student={student} periodKey={periodKey} gradeLevel={gradeLevel} isTopGrowth={topGrowthStudentId === student.id} />
          </Box>
        </Grid>)}
      </Grid>
    </Box>
  );
}
